import _defineProperty from "/Users/matt/dev/github.com/industriousapps/excelkits-client/node_modules/.pnpm/next@13.5.7_@babel+core@7.25.2_react-dom@18.3.1_react@18.3.1__react@18.3.1_sass@1.79.3/node_modules/next/dist/compiled/@babel/runtime/helpers/esm/defineProperty.js";

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

import { FloatingArrow, FloatingPortal } from '@floating-ui/react';
import { useClickAway } from 'react-use';
import { useMediaQuery } from 'react-responsive';
import breakpoints from '@/config/breakpoints';
import { useTheme } from '@/components/widget/Layout/ThemeProvider';
import useEditingAreasState from '@/components/Editor/hooks/useEditingAreasState';
import { deselectEditingArea, getContextEditingSectionAttr, getEditingSectionAttr } from '@/components/Editor/hooks/useEditingAreas';
import useEditingAreaPosition from '@/components/Editor/hooks/useEditingAreaPosition';
import { Drawer, DrawerContent } from '@/components/FormElements/internal/Drawer';
import EditSectionCollection from './Collection';
import EditChartAxes from './ChartAxes';
import EditChartLegends from './ChartLegends';
import { DISABLED_COLLECTION_EDITING_COMPONENTS, SUPPORTED_EDITING_SCOPES, SUPPORTED_SECTION_COMPONENTS } from './settings';
import styles from './styles.module.scss';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";

const ContextEditor = _ref => {
  var _widget$sections;

  let {
    widget,
    onChange
  } = _ref;
  const {
    styles: themeStyles
  } = useTheme();
  const {
    activeSectionId,
    activeSectionEditingArea,
    activeSectionEditingScope,
    activeEditingAreaMeta,
    activeClickPosition
  } = useEditingAreasState(); // get the position of the editing area

  const {
    arrowRef,
    floatingStyles,
    context,
    refs
  } = useEditingAreaPosition(_objectSpread({
    activeSectionId,
    activeSectionEditingArea,
    activeClickPosition,
    shift: false
  }, getPositionParams(activeSectionEditingScope, activeEditingAreaMeta))); // Responsive queries

  const isMobileOrTablet = useMediaQuery({
    maxWidth: breakpoints.tablet.maxWidth
  }); // close editor on outside click

  useClickAway(refs.floating, event => {
    const targetEl = event.target; // ignore clicks inside the nested floating UI portal

    if (targetEl && targetEl !== null && targetEl !== void 0 && targetEl.closest('[data-floating-ui-portal]')) {
      return;
    }

    if (targetEl && targetEl.closest(`[${getEditingSectionAttr()}]`)) return;
    deselectEditingArea();
  }); // render the editor if the active section editing scope is supported

  if (!SUPPORTED_EDITING_SCOPES.includes(activeSectionEditingScope) || !activeEditingAreaMeta) {
    return null;
  } // render the editor if the active section is supported


  const targetSection = (_widget$sections = widget.sections) === null || _widget$sections === void 0 ? void 0 : _widget$sections[activeSectionId];

  if (!targetSection || !SUPPORTED_SECTION_COMPONENTS.includes(targetSection.component)) {
    return null;
  } // render the editor if the active section editing scope is supported


  if (['collections', 'selections'].includes(activeSectionEditingScope) && DISABLED_COLLECTION_EDITING_COMPONENTS.includes(targetSection.component)) {
    return null;
  }

  let children;

  if (activeSectionEditingScope === 'chart-axes') {
    children = /*#__PURE__*/_jsx(EditChartAxes, {
      widgetId: widget.id || '',
      reportingWidgetId: widget.id || '',
      sectionId: targetSection.id || '',
      sectionComponent: targetSection.component,
      axis: activeEditingAreaMeta.axis,
      config: targetSection.config,
      variant: isMobileOrTablet ? 'inline' : 'floating',
      onChange: onChange
    });
  } else if (activeSectionEditingScope === 'chart-legends') {
    children = /*#__PURE__*/_jsx(EditChartLegends, {
      widgetId: widget.id || '',
      reportingWidgetId: widget.id || '',
      sectionId: targetSection.id || '',
      sectionComponent: targetSection.component,
      config: targetSection.config,
      variant: isMobileOrTablet ? 'inline' : 'floating',
      onChange: onChange
    });
  } else {
    children = /*#__PURE__*/_jsx(EditSectionCollection, {
      widgetId: widget.id || '',
      reportingWidgetId: widget.id || '',
      sectionId: targetSection.id || '',
      sectionComponent: targetSection.component,
      collectionIndex: activeEditingAreaMeta.collectionIndex,
      config: targetSection.config,
      variant: isMobileOrTablet ? 'inline' : 'floating',
      onChange: onChange
    });
  } // render the menu as a Drawer on mobile


  if (isMobileOrTablet) {
    return /*#__PURE__*/_jsx(Drawer, {
      open: true,
      onOpenChange: () => deselectEditingArea(),
      children: /*#__PURE__*/_jsx(DrawerContent, {
        className: styles.drawerContent,
        children: /*#__PURE__*/_jsx("div", {
          className: "w-full",
          [getContextEditingSectionAttr()]: targetSection.id || activeSectionId,
          children: children
        })
      })
    });
  }

  return (
    /*#__PURE__*/
    // ensure the floating element can appear outside any
    // potential parent containers that cause clipping
    _jsx(FloatingPortal, {
      children: /*#__PURE__*/_jsxs("div", {
        className: styles.floating,
        ref: refs.setFloating,
        style: floatingStyles,
        children: [/*#__PURE__*/_jsx("div", {
          className: "w-full",
          [getContextEditingSectionAttr()]: targetSection.id || activeSectionId,
          children: children
        }), /*#__PURE__*/_jsx(FloatingArrow, {
          ref: arrowRef,
          context: context,
          className: styles.arrow,
          strokeWidth: 1,
          stroke: themeStyles['gray-300']
        })]
      })
    })
  );
};

function getPositionParams(scope, meta) {
  if (scope === 'chart-axes' && (meta === null || meta === void 0 ? void 0 : meta.axis) === 'x') {
    return {
      placement: 'top',
      topPositionOffset: 12
    };
  }

  return {
    placement: 'right',
    leftPositionOffset: -24
  };
}

export default ContextEditor;